import CloseBlackIcon from 'assets/ver2/icons/close-black-icon.svg';
import DiscountEligibleIcon from 'assets/ver2/icons/discount-eligible-icon.svg';
import DiscountApplySuccessIcon from 'assets/icons/done-icon.svg';
import DiscountApplyFailIcon from 'assets/ver2/icons/alert-circle-icon.svg';
import { DESKTOP_MIN_WIDTH } from 'constants';
import { Fade, SlideInLeft } from 'helpers/animation';
import useLang from 'hooks/useLang';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  cursorPointerMixin,
  displayFlexRowCenterSpaceBetweenMixin,
  fontSize14Mixin,
  paragraph500Mixin,
  paragraphMixin,
} from 'styles';

export const DISCOUNT_ELIGIBILITY_TOAST_TYPE = {
  APPLY_FAIL: 'apply-fail',
  APPLY_SUCCESS: 'apply-success',
  ELIGIBILITY: 'eligibility',
};

// Icon + message per toast type. ELIGIBILITY is also the fallback for any
// unknown/undefined type, matching the prior ternary chain's default branch.
const DISCOUNT_ELIGIBILITY_TOAST_CONFIG = {
  [DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_FAIL]: {
    icon: DiscountApplyFailIcon,
    messageKey: 'label_discount_apply_toast_fail_msg',
  },
  [DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_SUCCESS]: {
    icon: DiscountApplySuccessIcon,
    messageKey: 'label_discount_apply_toast_success_msg',
  },
  [DISCOUNT_ELIGIBILITY_TOAST_TYPE.ELIGIBILITY]: {
    icon: DiscountEligibleIcon,
    messageKey: 'label_discount_eligibility_toast_msg',
  },
};

const withDiscountEligibilityToast = (Component) => {
  const DiscountEligibilityToastComponent = (props) => {
    const {
      discountApplyFailToastCode,
      discountEligibilityToastType = DISCOUNT_ELIGIBILITY_TOAST_TYPE.ELIGIBILITY,
      isForceHideDiscountEligibilityToast,
      onDiscountApplyFailToastAction,
    } = props || {};

    const { t } = useLang();

    const [isShowDiscountEligibilityToast, setShowDiscountEligibilityToast] =
      useState(false);

    const onForceHideDiscountEligibilityToast = () => {
      isForceHideDiscountEligibilityToast.current = true;
    };
    const handleOnClickDiscountEligibilityToastCloseButton = () => {
      onForceHideDiscountEligibilityToast();
      setShowDiscountEligibilityToast(false);
    };
    const renderDiscountEligibilityToast = ({
      discountApplyFailToastCode:
        toastDiscountCode = discountApplyFailToastCode,
      discountEligibilityToastType: toastType = discountEligibilityToastType,
      onDiscountApplyFailToastAction:
        handleDiscountApplyFailToastAction = onDiscountApplyFailToastAction,
      customStyles = {},
    } = {}) => {
      if (
        isForceHideDiscountEligibilityToast.current ||
        !isShowDiscountEligibilityToast
      )
        return null;

      const isApplyFailToast =
        toastType === DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_FAIL;
      const isApplySuccessToast =
        toastType === DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_SUCCESS;
      const { icon: toastIcon, messageKey: toastMessageKey } =
        DISCOUNT_ELIGIBILITY_TOAST_CONFIG[toastType] ||
        DISCOUNT_ELIGIBILITY_TOAST_CONFIG[
          DISCOUNT_ELIGIBILITY_TOAST_TYPE.ELIGIBILITY
        ];
      const handleOnClickToastMessage = (event) => {
        if (!isApplyFailToast) return;
        if (event.target?.tagName !== 'SPAN') return;

        handleDiscountApplyFailToastAction?.();
      };

      return (
        <DiscountEligibilityToastStyled
          $isActionable={isApplyFailToast}
          $isApplySuccessToast={isApplySuccessToast}
          $isApplyFailToast={isApplyFailToast}
          style={customStyles}
        >
          <img src={toastIcon} width={20} height={20} />
          <p
            onClick={handleOnClickToastMessage}
            dangerouslySetInnerHTML={{
              __html: isApplyFailToast
                ? t(toastMessageKey, {
                    code: toastDiscountCode,
                  })
                : t(toastMessageKey),
            }}
          />
          <DiscountEligibilityToastCloseButton
            src={CloseBlackIcon}
            width={20}
            height={20}
            onClick={handleOnClickDiscountEligibilityToastCloseButton}
          />
        </DiscountEligibilityToastStyled>
      );
    };

    const newProps = {
      ...props,
      renderDiscountEligibilityToast,
      setShowDiscountEligibilityToast,
      handleOnClickDiscountEligibilityToastCloseButton,
    };
    return <Component {...newProps} />;
  };
  return DiscountEligibilityToastComponent;
};

const DiscountEligibilityToastStyled = styled.div`
  padding-block: 5px;
  padding-inline: 16px;
  background-color: #e9ffe9;
  ${displayFlexRowCenterSpaceBetweenMixin};
  gap: 5px;
  width: 100%;

  & p {
    ${paragraph500Mixin};
    ${fontSize14Mixin};
  }

  & p span {
    ${(props) => props.$isActionable && cursorPointerMixin};
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    ${Fade};
    width: 436px;
    float: right;
    padding-block: 10px;
    margin-top: -13px;
    justify-content: start;
    ${SlideInLeft};

    & p {
      width: 100%;
    }
  }

  ${(props) =>
    props.$isApplySuccessToast &&
    `
      & p {
        ${paragraphMixin};
        color: #29695B;
      }
  `};

  ${(props) =>
    props.$isApplyFailToast &&
    `
    background-color: #FFD9D7;

      & p {
        ${paragraphMixin};
        color: #8F130B;
      }

      & p > span {
        font-weight: 500;
        text-decoration: underline;
      }
  `};
`;
const DiscountEligibilityToastCloseButton = styled.img`
  ${cursorPointerMixin};
`;

export default withDiscountEligibilityToast;
